import { CameraAlt } from '@mui/icons-material';
import { TFunction } from 'i18next';
import React from 'react';
import { ImageUpload } from '~/Components/ImageUpload';
import { RED_USC, WHITE } from '~/Constants/Colors.constants';
import { Route } from '~/Constants/Routes.constants';
import IUser, { getUserFullName } from '~/Models/DTO/User.model';
import { ProfilePictureCard } from '~/UIKit';
import { SettingSectionEnum } from '..';
import { ChangePassword } from '../ChangePassword';
import { IChangePassword } from '../useUserSettings';
import { UserProfileDetail } from './';
import { makeStyles } from '~/UIKit/Style';

const PROFILE_PICTURE_SIZE = 130;

const useStyles = makeStyles((theme) => ({
  userProfileContainer: {
    padding: 15,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: theme.breakpoints.values.sm,
    margin: 'auto',
  },
  column: {
    flex: 1,
    marginBottom: 15,

    '.MuiFormHelperText-root': {
      color: RED_USC,
      textAlign: 'right',
      marginBottom: 5,
    },

    [theme.breakpoints.down('lg')]: {
      marginBottom: 10,
      marginTop: 10,
    },
  },
  hide: {
    display: 'none',
  },
  uploadButton: {
    minWidth: 35,
    height: 35,
    borderRadius: '50%',
    position: 'absolute',
    marginTop: -35,
    marginLeft: 88,
    backgroundColor: WHITE,
    '&:hover': {
      backgroundColor: WHITE,
    },
  },
}));

export interface IUserProfileProps {
  profileInitialValues: Partial<IUser>;
  changePasswordInitialValues: IChangePassword;
  onValidateProfileForm: (data: Partial<IUser>) => void;
  onValidateChangePassword: (data: IChangePassword) => void;
  activeSection: SettingSectionEnum;
  onNavigate: (path: string) => void;
  t: TFunction;
  onProfilePictureUpdate: (imageDataUrl: string) => void;
  currentUser: IUser;
}

export const UserProfile = ({
  profileInitialValues,
  changePasswordInitialValues,
  onValidateProfileForm,
  onValidateChangePassword,
  onNavigate,
  activeSection,
  t,
  onProfilePictureUpdate,
  currentUser,
}: IUserProfileProps) => {
  const { classes } = useStyles();
  const fullName = getUserFullName(profileInitialValues as IUser);

  const onChangePassword = () => {
    onNavigate(Route.USER_SETTINGS_CHANGE_PASSWORD);
  };

  return (
    <div className={classes.userProfileContainer}>
      <div className={classes.column}>
        <ProfilePictureCard
          displayName={fullName}
          imageUrl={currentUser.image_url}
          textCenter
          fontSize={24}
          imageSize={PROFILE_PICTURE_SIZE}
          maxWidth={250}
          actionButton={
            <ImageUpload
              className={classes.uploadButton}
              onUpload={onProfilePictureUpdate}
              actionButtonIcon={<CameraAlt />}
              canvasStyle={{
                width: 300,
                height: 300,
                color: [0, 0, 0, 0.4],
                borderRadius: 500,
                border: 0,
              }}
            />
          }
        />
      </div>
      <div className={classes.column}>
        {activeSection === SettingSectionEnum.CHANGE_PASSWORD && (
          <ChangePassword
            t={t}
            classes={classes}
            changePasswordInitialValues={changePasswordInitialValues}
            onValidateChangePassword={onValidateChangePassword}
          />
        )}
        {activeSection === SettingSectionEnum.USER_PROFILE && (
          <UserProfileDetail
            handleChangePasswordNavigation={onChangePassword}
            profileInitialValues={profileInitialValues}
            onValidateProfileForm={onValidateProfileForm}
            t={t}
          />
        )}
      </div>
    </div>
  );
};
